import { useEffect } from 'react'
import axios from 'axios'
import { useMaintenanceStore, type MaintenanceInfo } from '@/stores/useMaintenanceStore'
import { apiRoot } from '@/lib/tenantHost'

const POLL_MS = 30_000
const ENDPOINT = `${apiRoot()}/v1/maintenance`

/**
 * Polls the public maintenance endpoint on its own axios instance, deliberately
 * bypassing apiClient: that client's interceptors would treat this call as
 * ordinary traffic, and during a lockout the poll is the only request that must
 * keep working.
 *
 * Runs on a slow interval normally and a fast one while locked out, so a user
 * who is staring at the maintenance screen gets back in without refreshing.
 */
export function useMaintenancePolling(): void {
  const setFromResponse = useMaintenanceStore((s) => s.setFromResponse)
  const lockedOut = useMaintenanceStore((s) => s.lockedOut)

  useEffect(() => {
    let cancelled = false

    const check = async (): Promise<void> => {
      try {
        const { data } = await axios.get<{ data: MaintenanceInfo }>(ENDPOINT, {
          withCredentials: true,
        })
        if (!cancelled && data?.data) setFromResponse(data.data)
      } catch {
        // The endpoint being unreachable tells us nothing about maintenance —
        // it is far more likely the network. Leave the current state alone.
      }
    }

    void check()
    const interval = setInterval(() => void check(), lockedOut ? POLL_MS : POLL_MS * 4)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [setFromResponse, lockedOut])
}
